import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    ContentCard,
    ConvertedCurrencyValue,
    DefaultErrorBoundary,
    LocalizedDateTimeFromUtc,
    LocalizedUtcTimeUnit,
    LocalizedUtcTimeUnitFormat,
} from '@mews-commander/ui';
import { ColumnAlignment } from '@mews/b2b-ui';
import { ApplicationState, OrderItem } from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import { isCurrentEnterpriseInMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getServiceTimeUnitPluralKey } from '@mews-commander/legacy-entities/src/utils/serviceTimeUnitLocalization';
import { getTimeUnitByServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import { Table, TableWrapperElement } from '@mews-commander/ui/legacy';
import { BillLink } from '../../../../components/commander-ui';
import { getSortedAllDefaultProductOrderItems } from '../../selectors/orderItems';
import { PartialMonthDayRange } from './PartialMonthDayRange';

const createTableMapping = (timeUnit: $TSFixMe) => [
    {
        title: <LocalizedText textKey={TextKey.Date} />,
        valueKey: 'startUtc',
        columnWidth: '20%',
        render: ({ startUtc, proRataStartUtc, proRataEndUtc }: $TSFixMe) =>
            proRataStartUtc && proRataEndUtc ? (
                <PartialMonthDayRange proRataStartUtc={proRataStartUtc} proRataEndUtc={proRataEndUtc} showDaysCount />
            ) : (
                <LocalizedUtcTimeUnit
                    format={LocalizedUtcTimeUnitFormat.Short}
                    timeUnit={timeUnit}
                    utcString={startUtc}
                />
            ),
    },
    {
        title: <LocalizedText textKey={TextKey.Cost} />,
        valueKey: 'amount',
        columnAlignment: ColumnAlignment.Right,
        columnWidth: '20%',
        render: ({ totalAmount, isMultiTaxEnvironment }: $TSFixMe) => (
            <ConvertedCurrencyValue amount={totalAmount} net={isMultiTaxEnvironment} />
        ),
    },
    {
        title: <LocalizedText textKey={TextKey.Consumed} />,
        valueKey: 'consumedUtc',
        columnWidth: '25%',
        render: ({ consumedUtc }: $TSFixMe) => <LocalizedDateTimeFromUtc utcString={consumedUtc} />,
    },
    {
        title: <LocalizedText textKey={TextKey.Bill} />,
        valueKey: 'bill',
        render: ({ id, groupId }: $TSFixMe) => <BillLink orderItemId={id} groupId={groupId} />,
    },
    {
        title: ' ',
        valueKey: 'isCanceled',
        columnAlignment: ColumnAlignment.Right,
        render: ({ isCanceled }: $TSFixMe) => (isCanceled ? <LocalizedText textKey={TextKey.Canceled} /> : null),
    },
];

const getTableSource = (nights: OrderItem[], isMultiTaxEnvironment: boolean, groupId: string) =>
    nights.map((night) => ({
        ...night,
        groupId,
        isMultiTaxEnvironment,
    }));

interface OwnProps {
    serviceId: string;
    reservationId: string;
    groupId: string;
}

interface StateProps {
    isMultiTaxEnvironment: $TSFixMe;
    timeUnitKey?: Mews.TextKey;
    timeUnit?: TimeUnit;
    nights: OrderItem[];
}

type ItemsNightsTableProps = OwnProps & StateProps;

const ItemsNightsTable = ({ nights, isMultiTaxEnvironment, timeUnitKey, timeUnit, groupId }: ItemsNightsTableProps) => {
    const tableSource = getTableSource(nights, isMultiTaxEnvironment, groupId);
    const tableMapping = createTableMapping(timeUnit);
    return (
        <DefaultErrorBoundary>
            <ContentCard title={timeUnitKey && <LocalizedText textKey={timeUnitKey} />} data-test-items-night-table>
                <TableWrapperElement>
                    <Table mapping={tableMapping} source={tableSource} />
                </TableWrapperElement>
            </ContentCard>
        </DefaultErrorBoundary>
    );
};

export default connect<StateProps, {}, OwnProps, ApplicationState>(
    createStructuredSelector({
        isMultiTaxEnvironment: isCurrentEnterpriseInMultiTaxEnvironment,
        timeUnitKey: (state, { serviceId }) => getServiceTimeUnitPluralKey(state, { id: serviceId }),
        timeUnit: getTimeUnitByServiceId,
        nights: (state, { serviceId, reservationId }) =>
            getSortedAllDefaultProductOrderItems(state, { serviceId, serviceOrderIds: [reservationId] }),
    })
)(ItemsNightsTable);
